import React from 'react';
import NumberFormat from 'react-number-format';

import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { PaymentItemModel } from '../../../../../shared/models/payment-item.model';
import { PaymentModel } from '../../../../../shared/models/payment.model';

/** Fee names the payment service gives the lines it derives, rather than copies. */
const LANDLORD_AMOUNT = 'landlordAmount';
const BIRDNEST_AMOUNT = 'birdnestAmount';
const VAT = 'VAT';

export interface PaymentVatBreakdownModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    payment: PaymentModel | null;
  };
  eventHandlers: {
    onClose: () => void;
  };
}

/** Amounts arrive as strings from the API, so every read goes through here. */
function amountOf(item: PaymentItemModel): number {
  const value = Number(item.feeValue);
  return Number.isFinite(value) ? value : 0;
}

function totalOf(items: PaymentItemModel[]): number {
  return (items || []).reduce((total, item) => total + amountOf(item), 0);
}

function prefixFor(currency?: string): string {
  return currency ? `${CURRENCY[currency as keyof typeof CURRENCY]} ` : 'EGP ';
}

/**
 * The breakdown carries its own currency, which is not always the payment's: a
 * channel-manager reservation is priced in USD and restated here in EGP at the payment's
 * own rate. Reading the prefix off the payment would label those figures as dollars.
 */
function breakdownCurrency(items: PaymentItemModel[], payment: PaymentModel): string | undefined {
  return (items && items.length > 0 && items[0].currency) || payment.currency;
}

function labelFor(item: PaymentItemModel): string {
  if (item.feeName === LANDLORD_AMOUNT) return "Landlord's share";
  if (item.feeName === BIRDNEST_AMOUNT) return "Birdnest's share";
  return item.feeName;
}

function noteFor(item: PaymentItemModel): string {
  if (item.feeName === LANDLORD_AMOUNT) return 'paid gross — no VAT';
  if (item.feeName === VAT) return 'carved out of the lines above';
  return '';
}

export const PaymentVatBreakdownModal: React.FC<PaymentVatBreakdownModalProps> = ({
  config: { showModal },
  data: { payment },
  eventHandlers: { onClose }
}) => {
  if (!payment) {
    return <></>;
  }

  const items: PaymentItemModel[] = payment.paymentItemsWithVatApplied || [];
  const currency = breakdownCurrency(items, payment);
  const prefix = prefixFor(currency);
  const vatTotal = totalOf(items.filter((item) => item.feeName === VAT));
  const breakdownTotal = totalOf(items);
  const originalTotal = totalOf(payment.paymentItems);
  // Only comparable when both sides are in the same currency — a converted breakdown is
  // deliberately a different number from the payment items.
  const wasConverted =
    !!currency &&
    !!payment.currency &&
    String(currency).toLowerCase() !== String(payment.currency).toLowerCase();

  const headerCell: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase'
  };

  return (
    <Modal
      config={{ showModal, fullScreen: false, maxWidth: 'md', showClose: true, autoHeight: true }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div style={{ padding: '8px 4px' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>
          VAT breakdown
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>
          Payment {payment.id} — rent split between the landlord and Birdnest, with VAT carved out
          of every line except the landlord&apos;s.
        </div>

        {wasConverted && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: '#eff6ff',
              color: '#1D4ED8',
              fontSize: '13px'
            }}
          >
            Charged in {String(payment.currency).toUpperCase()} and shown here in{' '}
            {String(currency).toUpperCase()}, converted at the rate stored on this payment.
          </div>
        )}

        {items.length === 0 ? (
          <div
            style={{
              border: '1px dashed #e5e7eb',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '14px'
            }}
          >
            No VAT breakdown on this payment yet.
          </div>
        ) : (
          <>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  padding: '10px 16px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  ...headerCell
                }}
              >
                <div>Item</div>
                <div>Type</div>
                <div style={{ textAlign: 'right' }}>Amount</div>
              </div>

              {items.map((item, index) => {
                const isVat = item.feeName === VAT;
                const isLandlord = item.feeName === LANDLORD_AMOUNT;
                const note = noteFor(item);

                return (
                  <div
                    key={`${item.feeName}-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      padding: '12px 16px',
                      borderBottom: index < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                      backgroundColor: isVat ? '#f9fafb' : isLandlord ? '#fffbeb' : 'white',
                      fontSize: '14px',
                      color: '#374151',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: isVat || isLandlord ? 600 : 400 }}>
                        {labelFor(item)}
                      </div>
                      {note && (
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                          {note}
                        </div>
                      )}
                    </div>
                    <div>{item.itemType || 'N/A'}</div>
                    <div style={{ textAlign: 'right', fontWeight: isVat ? 600 : 500 }}>
                      <NumberFormat
                        value={amountOf(item)}
                        displayType="text"
                        thousandSeparator={true}
                        decimalScale={2}
                        prefix={prefix}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginTop: '16px',
                fontSize: '14px',
                color: '#374151'
              }}
            >
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Total VAT</div>
                <div style={{ fontWeight: 600 }}>
                  <NumberFormat
                    value={vatTotal}
                    displayType="text"
                    thousandSeparator={true}
                    decimalScale={2}
                    prefix={prefix}
                  />
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Total charged</div>
                <div style={{ fontWeight: 600 }}>
                  <NumberFormat
                    value={breakdownTotal}
                    displayType="text"
                    thousandSeparator={true}
                    decimalScale={2}
                    prefix={prefix}
                  />
                </div>
              </div>
            </div>

            {/* The breakdown restates the same money, so a mismatch means something is
                stale — say so rather than letting finance reconcile it by hand. */}
            {!wasConverted && Math.abs(breakdownTotal - originalTotal) > 0.01 && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: '#fef2f2',
                  color: '#B91C1C',
                  fontSize: '13px'
                }}
              >
                This breakdown totals{' '}
                <NumberFormat
                  value={breakdownTotal}
                  displayType="text"
                  thousandSeparator={true}
                  decimalScale={2}
                  prefix={prefix}
                />{' '}
                but the payment items total{' '}
                <NumberFormat
                  value={originalTotal}
                  displayType="text"
                  thousandSeparator={true}
                  decimalScale={2}
                  prefix={prefix}
                />
                . The breakdown may be out of date.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PaymentVatBreakdownModal;
